import { Link, useLocation } from 'react-router-dom'
import { menuItemsDefaults } from './data.js'
import logoLuca from '../../../assets/logoNuevo/003 Logo blanco.svg'
import isoLogo from '../../../assets/logoNuevo/007 Isotipo blanco.svg'
import { Formik } from 'formik'
import { useGlobalContext } from '../../../context/GlobalState'
import { sileo } from 'sileo'
import {
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  PushPinOutlined as PushPinOutlinedIcon
} from '@mui/icons-material'
import { useSidebarContext } from './SidebarContext.js'
import { useQueryGetUserMe } from '../../../queries/userQueries'
import { useMemo } from 'react'
import PlanRestrictedButton from '../../modules/ui/PlanRestrictedButton'
import { AdminPanelSidebarButton } from './AdminPanelSidebarButton'

const getPlanRestrictionMessage = (permission) => {
  if (permission === 'allowIntelligentBilling') return 'La facturación'
  if (permission === 'allowAccountingDesk') return 'Mi Contador'
  return 'Esta funcionalidad'
}

const Sidebar = () => {
  const { currentBusiness } = useGlobalContext()
  const location = useLocation()
  const currentPath = location.pathname
  const { isExpanded, isPinned, isMobile, toggleSidebar, togglePinSidebar } = useSidebarContext()
  const { data: userMe } = useQueryGetUserMe()
  const isExpandedView = isExpanded && !isMobile

  const isAdmin = userMe?.role?.admin === true
  const userEmail = userMe?.email?.trim().toLowerCase()

  const filteredMenuItems = useMemo(() => {
    return menuItemsDefaults.filter(menuItem => {
      if (menuItem.allowedEmails) {
        return !!userEmail && menuItem.allowedEmails.includes(userEmail)
      }
      if (menuItem.showForAdmin !== undefined && menuItem.showForNonAdmin !== undefined) {
        if (isAdmin) {
          return menuItem.showForAdmin
        } else {
          return menuItem.showForNonAdmin
        }
      }
      return true
    })
  }, [isAdmin, userEmail])

  const NavLinks = () =>
    filteredMenuItems.map(menuItem => {
      const isActive = menuItem.path && currentPath === menuItem.path
      const IconComponent = menuItem.icon

      const menuItemContent = (
        <>
          <div
            className={`flex justify-center items-center w-10 h-10 rounded-full transition-all ${
              isActive ? '' : 'bg-transparent'
            }`}
          >
            <IconComponent
              className='w-6 h-6'
              style={{ fontSize: '1.5rem', width: '1.5rem', height: '1.5rem' }}
              {...(IconComponent.name === 'ReactComponent' ? { alt: menuItem.name } : {})}
            />
          </div>
          {isExpandedView && (
            <p className="text-[13px] md:text-sm font-semibold flex items-center gap-2">
              {menuItem.name}
              {menuItem.badge && (
                <span className='bg-white text-gray-600 text-[10px] px-2 py-[-3px] rounded-full border border-gray-300'>
                  {menuItem.badge}
                </span>
              )}
            </p>
          )}
        </>
      )

      if (!menuItem.path) {
        const menuItemElement = (
          <div
            className={`
              flex items-center gap-4 mb-2 hd:mb-4 py-1 px-3 rounded-full transition-all relative
              text-white/50 cursor-not-allowed
              ${!isExpandedView ? 'p-4 w-12 h-12' : 'px-4 py-2'}
            `}
          >
            {menuItemContent}
          </div>
        )

        if (menuItem.requiredPermission) {
          return (
            <PlanRestrictedButton
              key={menuItem.id}
              requiredPermission={menuItem.requiredPermission}
              message={getPlanRestrictionMessage(menuItem.requiredPermission)}
              keepEnabled={true}
              canAddAddon={menuItem.requiredPermission === 'allowIntelligentBilling'}
            >
              {menuItemElement}
            </PlanRestrictedButton>
          )
        }

        return (
          <div key={menuItem.id}>
            {menuItemElement}
          </div>
        )
      }

      const linkElement = (
        <Link
          key={menuItem.id}
          to={menuItem.path}
          className={`
            flex items-center gap-4 mb-2 hd:mb-4 py-1 px-3 transition-all relative
            ${isActive ? 'rounded-md bg-white text-primary shadow-lg' : 'rounded-full text-white'}
            ${!isActive ? 'hover:bg-white/35 hover:scale-110 active:scale-100' : ''}
            ${!isExpandedView ? 'p-4 w-12 h-12' : 'px-4 py-2'}
          `}
        >
          {menuItemContent}
        </Link>
      )

      if (menuItem.requiredPermission) {
        return (
          <PlanRestrictedButton
            key={menuItem.id}
            requiredPermission={menuItem.requiredPermission}
            message={getPlanRestrictionMessage(menuItem.requiredPermission)}
            keepEnabled={true}
            canAddAddon={menuItem.requiredPermission === 'allowIntelligentBilling'}
          >
            {linkElement}
          </PlanRestrictedButton>
        )
      }

      return linkElement
    })

  const ContactSection = () => {
    return (
      <div className='flex flex-col gap-6 text-white/70'>
        <Formik
          initialValues={{ message: '' }}
          onSubmit={async (values, actions) => {
            try {
              // Se construye un objeto FormData para enviar la petición
              const formData = new FormData()
              formData.append('from', currentBusiness.email)
              formData.append('organizationId', currentBusiness.id)
              formData.append('bussinesName', currentBusiness.name)
              formData.append('bussinesRut', currentBusiness.rut)
              formData.append('bussinesPhone', currentBusiness.phone)
              formData.append('message', values.message)

              // Envío de la petición usando fetch sin headers extra
              await sileo.promise(
                fetch('https://formsubmit.co/ajax/soporte@tryluca.cl', {
                  method: 'POST',
                  body: formData
                }).then(async response => {
                  if (!response.ok) {
                    // Se captura cualquier error de la respuesta
                    const errorData = await response.text()
                    throw new Error(errorData || 'Error en la respuesta del servidor')
                  }
                  return response.json()
                }),
                {
                  loading: { title: 'Enviando Mensaje' },
                  success: {
                    title: 'Mensaje Enviado',
                    description: (
                      <span className='text-green-500/80 font-medium'>
                        Mensaje enviado con éxito 👌
                      </span>
                    )
                  },
                  error: {
                    title: 'Error de Envío',
                    description: 'Ocurrió un error al enviar el mensaje 🤯'
                  }
                }
              )
            } catch (error) {
              console.error('Error al enviar el mensaje:', error)
            }
            actions.setSubmitting(false)
            actions.resetForm()
          }}
        ></Formik>
        <div className='mt-4 border-t border-white/80 w-1/2 py-2'>tryluca.cl</div>
      </div>
    )
  }

  return (
    <div
      className={`
         shrink-0 transition-all duration-300 sticky top-0
         flex flex-col justify-between items-stretch
         ${isExpandedView ? 'w-64' : 'w-14 sm:w-20'}
         bg-primary text-secondary p-2 z-30 font-sans
         h-full max-h-full overflow-y-auto
     `}
    >
      <div className='flex justify-between mb-2'>
        <button
          onClick={() => !isMobile && toggleSidebar()}
          className='text-white hover:text-gray-400'
        >
          {isExpandedView ? <KeyboardArrowLeftOutlined /> : <KeyboardArrowRightOutlined />}
        </button>
        {isExpandedView && (
          <button
            onClick={togglePinSidebar}
            className={`ml-2 ${isPinned ? 'text-accent2' : 'text-white'} hover:text-gray-400`}
          >
            <PushPinOutlinedIcon />
          </button>
        )}
      </div>
      <div className='flex justify-center mb-4 hd:mb-10'>
        <Link to='/' className='inline-flex items-center'>
          {isExpandedView ? (
            <img src={logoLuca} alt='Logo' className='w-42 h-14' />
          ) : (
            <img src={isoLogo} alt='IsoLogo' className='h-16' />
          )}
        </Link>
      </div>
      <NavLinks />
      {userMe?.platformRole && <AdminPanelSidebarButton isExpandedView={isExpandedView} />}
      {isExpandedView && (
        <div className='mt-auto text-white/70 gap-4 text-xs'>
          <ContactSection />
        </div>
      )}
    </div>
  )
}

export default Sidebar
